import { useCallback, useRef } from 'react';
import { useBackClose } from './useBackClose';

/**
 * Two-level OS-back handling for the mobile sidebar drawer.
 *
 *   settings --back--> nav --back--> drawer closed
 *
 * useBackClose attaches a WINDOW-level popstate listener per active instance,
 * so two naively nested instances would both fire on a single back gesture and
 * collapse both levels at once. The outer instance therefore checks the current
 * view through a ref (never stale) and defers to the inner one while Settings
 * is open. Listeners fire in registration order, so the outer always runs and
 * bails before the inner acts.
 *
 * THE HISTORY STACK IS THE ONLY CHANNEL.
 * ---------------------------------------
 * Every dismissal — OS gesture, the on-screen back arrow, a backdrop tap —
 * is driven by a history traversal, and the resulting popstate is what moves
 * the React state. Callers must therefore use the two functions returned here
 * instead of flipping the view/open state themselves:
 *
 *   goBack()       settings -> nav      (history.back(), one entry)
 *   requestClose() -> drawer closed     (history.back() / go(-2))
 *
 * Why this matters — the two bugs it exists to prevent:
 *
 * 1. Setting the view to 'nav' directly made useBackClose's cleanup fire a
 *    history.back() of its own. That real popstate arrived AFTER the inner
 *    listener had been detached but while the OUTER one was still attached,
 *    and by then viewRef already said 'nav', so the outer guard no longer
 *    bailed — one tap on the back arrow closed the whole drawer.
 *
 * 2. Closing straight out of Settings flipped `open` and `view` in a single
 *    render, so both instances deactivated together. History traversal is
 *    asynchronous, so when the outer cleanup ran, history.state was still the
 *    INNER sentinel; its id check failed and it skipped its own back(). One
 *    history entry was stranded per open->settings->close cycle — a dead back
 *    press for the user each time.
 *
 * Both disappear once nothing unwinds the levels except a genuine popstate:
 * every useBackClose instance is then closed *by* a pop (`closedByPop`), so no
 * cleanup ever has to pop on its own behalf.
 */
export function useDrawerBackNavigation({ active, view, onBack, onClose }) {
  const viewRef = useRef(view);
  viewRef.current = view;
  const activeRef = useRef(active);
  activeRef.current = active;
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Set while we collapse BOTH levels with a single history.go(-2): that
  // traversal fires ONE popstate, and it must close the drawer rather than
  // step back to the nav view, so the inner listener stands down for it.
  const unwindingRef = useRef(false);

  // Outer level: closes the drawer, but only when Settings is not showing.
  useBackClose(active, () => {
    if (unwindingRef.current) {
      // Clear on a macrotask: the inner listener still has to see the flag
      // for this same popstate dispatch, and onClose() below may re-render
      // synchronously (unbatched native event) and detach it first.
      setTimeout(() => {
        unwindingRef.current = false;
      }, 0);
      onCloseRef.current();
      return;
    }
    if (viewRef.current === 'settings') return;
    onCloseRef.current();
  });

  // Inner level: one step deeper — returns to the nav view. Its own history
  // entry is consumed by the pop, leaving the outer entry for the next back.
  useBackClose(active && view === 'settings', () => {
    if (unwindingRef.current) return; // the outer level owns this pop
    onBackRef.current();
  });

  // True when the entry on top of the history stack is one of ours. If some
  // other navigation (a router push, another overlay) landed on top, we must
  // NOT traverse — we'd undo it. In that case fall back to the plain callback
  // and let useBackClose's own cleanup do its (already correct) bookkeeping.
  const ownsTopEntry = () =>
    !!(window.history.state && window.history.state.__backClose);

  /** On-screen back arrow: identical channel to the OS back gesture. */
  const goBack = useCallback(() => {
    unwindingRef.current = false;
    if (activeRef.current && viewRef.current === 'settings' && ownsTopEntry()) {
      window.history.back();
      return;
    }
    onBackRef.current();
  }, []);

  /** Backdrop tap / nav-link dismissal: unwind however many levels are open. */
  const requestClose = useCallback(() => {
    unwindingRef.current = false;
    if (!activeRef.current || !ownsTopEntry()) {
      onCloseRef.current();
      return;
    }
    if (viewRef.current === 'settings') {
      unwindingRef.current = true;
      window.history.go(-2);
      return;
    }
    window.history.back();
  }, []);

  return { goBack, requestClose };
}

export default useDrawerBackNavigation;
